import {
  Skeleton,
  SkeletonButton,
  SkeletonCard,
  SkeletonFormField,
  SkeletonText,
  SkeletonTitle,
} from './SkeletonComponents';

// Expense Detail skeleton
export function ExpenseDetailSkeleton() {
  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Header - matching the real component */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="shrink-0 mt-1">
            {/* Back button */}
            <Skeleton className="w-10 h-10 rounded-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-1">
              {/* Title with emoji space */}
              <SkeletonTitle className="w-48 h-8" />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {/* Group link */}
              <SkeletonText className="w-32" />
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {/* Edit button */}
          <SkeletonButton className="w-full sm:w-20" />
          {/* Delete button */}
          <SkeletonButton className="w-full sm:w-24" />
        </div>
      </div>

      {/* Expense Details - Single Column as in real component */}
      <div className="space-y-4">
        {/* Main expense card - ExpenseCard component */}
        <SkeletonCard className="p-6 rounded-lg">
          {/* Header section with icon */}
          <div className="flex items-start gap-3 mb-4">
            {/* Category icon */}
            <Skeleton className="w-12 h-12 rounded-lg shrink-0" />
            <div className="flex-1">
              {/* First line: Payer name and timestamp */}
              <div className="flex items-center justify-between gap-2">
                <SkeletonText className="w-24" />
                <SkeletonText className="w-16" />
              </div>

              {/* Second line: paid amount for title */}
              <div className="mt-1">
                <span className="inline-block">
                  <SkeletonText className="w-8 inline-block" />
                </span>
                <span className="inline-block mx-1">
                  <SkeletonText className="w-20 inline-block font-bold" />
                </span>
                <span className="inline-block">
                  <SkeletonText className="w-8 inline-block" />
                </span>
                <span className="inline-block mx-1">
                  <SkeletonText className="w-32 inline-block font-semibold" />
                </span>
              </div>

              {/* Split info line */}
              <div className="mt-0.5">
                <SkeletonText className="w-48 text-xs" />
              </div>

              {/* Group name link */}
              <div className="mt-1">
                <SkeletonText className="w-24 text-xs" />
              </div>
            </div>
          </div>

          {/* Category and date info */}
          <div className="flex items-center gap-2 -mt-2 mb-3 text-xs ml-12">
            <SkeletonText className="w-16" />
            <span className="text-gray-400">•</span>
            <SkeletonText className="w-12" />
            <span className="text-gray-400">•</span>
            <SkeletonText className="w-20" />
          </div>

          {/* Notes */}
          <div className="mb-4 p-4 bg-[#F6F5F3] dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
            <Skeleton className="w-full h-4 mb-2" />
            <Skeleton className="w-3/4 h-4" />
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 dark:border-gray-700 mt-4 mb-5"></div>

          {/* You owe section - UserPositionIndicator */}
          <div className="px-4 py-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <SkeletonText className="w-16 text-xs mb-1" />
            <SkeletonText className="w-24 text-xl font-bold" />
          </div>

          {/* Social section */}
          <div className="mt-3">
            {/* Reactions */}
            <div className="mb-3">
              <div className="flex items-center gap-2">
                {[1, 2].map(i => (
                  <Skeleton key={i} className="w-16 h-8 rounded-full" />
                ))}
                <Skeleton className="w-24 h-8 rounded-full" />
              </div>
            </div>
            {/* Comment input */}
            <div className="flex items-start gap-2">
              <Skeleton className="w-8 h-8 rounded-full" />
              <Skeleton className="flex-1 h-10 rounded-lg" />
            </div>
          </div>
        </SkeletonCard>

        {/* How it's split Card - separate card */}
        <SkeletonCard className="p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            <Skeleton className="w-32 h-6 inline-block" />
          </h3>

          {/* Split method highlight box */}
          <div className="px-4 py-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-lg mb-4">
            <div className="flex items-center gap-3">
              <Skeleton className="w-6 h-6" />
              <div className="flex-1">
                <SkeletonText className="w-48 text-xl font-bold" />
                <SkeletonText className="w-64 text-sm mt-0.5" />
              </div>
            </div>
          </div>

          {/* Split entries */}
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <div className="flex items-center gap-2">
                      <SkeletonText className="w-24 text-sm font-medium" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <SkeletonText className="w-8 text-xs" />
                    <SkeletonText className="w-20 text-sm font-semibold" />
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <Skeleton className="h-full w-1/3 rounded-full bg-gray-300 dark:bg-gray-600" />
                </div>
              </div>
            ))}
          </div>

          {/* Summary row */}
          <div className="mt-4 px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg">
            <div className="flex items-center justify-between">
              <SkeletonText className="w-12 text-xs uppercase" />
              <SkeletonText className="w-24 text-sm font-bold" />
            </div>
          </div>
        </SkeletonCard>
      </div>
    </div>
  );
}

// Expense Form skeleton - used for both create and edit forms
export function ExpenseFormSkeleton() {
  return (
    <div className="min-h-screen bg-canvas dark:bg-canvas-dark">
      <div className="max-w-2xl mx-auto p-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <SkeletonButton className="w-10 h-10" />
            <div>
              <SkeletonTitle className="w-32 mb-2" />
              <SkeletonText className="w-48" />
            </div>
          </div>
        </div>

        {/* Form Card */}
        <SkeletonCard className="p-6">
          <div className="space-y-6">
            {/* Basic Info Section */}
            <div className="space-y-4">
              <SkeletonFormField />
              <SkeletonFormField />

              {/* Amount and Currency Grid */}
              <div className="grid grid-cols-2 gap-4">
                <SkeletonFormField />
                <SkeletonFormField />
              </div>

              <SkeletonFormField />

              {/* Notes textarea */}
              <div>
                <Skeleton className="w-12 h-3 mb-2" />
                <Skeleton className="w-full h-24 rounded-lg" />
              </div>
            </div>

            {/* Payers Section */}
            <div>
              <SkeletonTitle className="w-24 mb-4" />
              <div className="space-y-3">
                {[1, 2].map(i => (
                  <div key={i} className="flex gap-4">
                    <Skeleton className="flex-1 h-12 rounded-lg" />
                    <Skeleton className="w-32 h-12 rounded-lg" />
                  </div>
                ))}
              </div>
              <SkeletonButton className="w-32 mt-2" />
            </div>

            {/* Split Section */}
            <div>
              <SkeletonTitle className="w-24 mb-4" />

              {/* Split type buttons */}
              <div className="flex gap-2 mb-4">
                <Skeleton className="w-24 h-10 rounded-lg" />
                <Skeleton className="w-24 h-10 rounded-lg" />
                <Skeleton className="w-24 h-10 rounded-lg" />
              </div>

              {/* Split members */}
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-5 h-5 rounded-sm" />
                      <Skeleton className="w-8 h-8 rounded-full" />
                      <Skeleton className="w-24 h-4" />
                    </div>
                    <Skeleton className="w-20 h-8 rounded-sm" />
                  </div>
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-4 pt-4">
              <SkeletonButton className="w-24" />
              <SkeletonButton className="w-32" />
            </div>
          </div>
        </SkeletonCard>
      </div>
    </div>
  );
}
